import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaBrain, FaCheckCircle, FaEye, FaRedo, FaThumbsDown } from 'react-icons/fa';
import * as Toast from '@radix-ui/react-toast';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import {
  DictionaryWord,
  fetchReviewQueue,
  submitReviewResult,
} from '../components/chat/api/dictionaryApi';

type RatingOption = {
  value: number;
  labelKey: string;
  descriptionKey: string;
  className: string;
};

const RATING_OPTIONS: RatingOption[] = [
  { value: 1, labelKey: 'review.ratings.again', descriptionKey: 'review.ratings.againDesc', className: 'btn-error' },
  { value: 3, labelKey: 'review.ratings.hard', descriptionKey: 'review.ratings.hardDesc', className: 'btn-warning' },
  { value: 4, labelKey: 'review.ratings.good', descriptionKey: 'review.ratings.goodDesc', className: 'btn-primary' },
  { value: 5, labelKey: 'review.ratings.easy', descriptionKey: 'review.ratings.easyDesc', className: 'btn-success' },
];

const ReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectedLanguage } = useLanguage();
  const languageCode = selectedLanguage?.iso639_tag;

  const [queue, setQueue] = useState<DictionaryWord[]>([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [lastOutcome, setLastOutcome] = useState<{
    word: string;
    rating: number;
    interval: number;
    nextReviewAt: string | null;
  } | null>(null);
  const timerRef = React.useRef(0);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchReviewQueue(languageCode, 10);
      setQueue(response.cards);
      setAvailableCount(response.available_count);
    } catch (fetchError: any) {
      console.error('Failed to load review queue:', fetchError);
      setError(fetchError?.message || 'Failed to load review queue');
      setQueue([]);
      setAvailableCount(0);
    } finally {
      setLoading(false);
      setShowAnswer(false);
    }
  }, [languageCode]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const currentCard = queue.length > 0 ? queue[0] : null;

  const handleReveal = () => setShowAnswer(true);

  const handleRating = async (value: number) => {
    if (!currentCard) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const updatedCard = await submitReviewResult(currentCard.user_marked_word_id, value);
      setLastOutcome({
        word: currentCard.word_base_form,
        rating: value,
        interval: updatedCard.interval,
        nextReviewAt: updatedCard.next_review_at,
      });

      // Show toast notification
      setToastOpen(false);
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setToastOpen(true);
      }, 100);

      setQueue((prev) => prev.slice(1));
      setAvailableCount((prev) => Math.max(prev - 1, 0));
      setShowAnswer(false);

      if (queue.length <= 1) {
        // Reload to pick up newly due cards or confirm completion
        await loadQueue();
      }
    } catch (submitError: any) {
      console.error('Failed to submit review:', submitError);
      setError(submitError?.message || 'Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatRelative = (dateString: string | null): string => {
    if (!dateString) return t('review.relativeTime.rightAway');
    const target = new Date(dateString);
    if (Number.isNaN(target.getTime())) return t('review.relativeTime.unknown');
    const diffMs = target.getTime() - Date.now();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    if (Math.abs(diffDays) < 1) {
      const diffHours = diffMs / (1000 * 60 * 60);
      if (Math.abs(diffHours) < 1) {
        const diffMinutes = diffMs / (1000 * 60);
        return formatter.format(Math.round(diffMinutes), 'minute');
      }
      return formatter.format(Math.round(diffHours), 'hour');
    }
    return formatter.format(Math.round(diffDays), 'day');
  };

  const reviewSummary = useMemo(() => {
    if (!lastOutcome) return null;
    const ratingOption = RATING_OPTIONS.find((option) => option.value === lastOutcome.rating);
    const ratingLabel = ratingOption ? t(ratingOption.labelKey) : `Rating ${lastOutcome.rating}`;
    return {
      ...lastOutcome,
      ratingLabel,
      nextReviewText: formatRelative(lastOutcome.nextReviewAt),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastOutcome, t]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const headerTitle = selectedLanguage
    ? `${t('review.title')} · ${selectedLanguage.language_name}`
    : t('review.title');

  return (
    <Toast.Provider swipeDirection="right" duration={3000}>
      <div className="flex flex-col h-full pt-16 pb-[7rem] px-3 sm:px-6 bg-base-100">
        <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col gap-4">
          <header className="mt-4">
            <div className="flex items-center gap-3">
              <FaBrain className="text-3xl text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{headerTitle}</h1>
                <p className="text-sm text-base-content/70">
                  {availableCount > 0
                    ? t('review.cardsDue', { count: availableCount })
                    : t('review.noDueCards')}
                </p>
              </div>
            </div>
          </header>

          {error && (
            <div className="alert alert-error shadow-sm">
              <FaThumbsDown className="h-5 w-5" />
              <span>{error}</span>
              <div>
                <button className="btn btn-sm btn-ghost" onClick={loadQueue}>
                  {t('common.retry')}
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <span className="loading loading-spinner loading-lg text-primary" />
            </div>
          ) : currentCard ? (
            <div className="flex-1 flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-base-content/60">
                  {t('review.dueQueue')}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={loadQueue}
                    className="btn btn-ghost btn-sm gap-2"
                    disabled={loading || isSubmitting}
                  >
                    <FaRedo /> {t('common.refresh')}
                  </button>
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-8">
                <div className="flex items-start justify-between text-sm text-base-content/60">
                  <div>
                    <p>{t('review.interval')} <strong className="text-base-content">{currentCard.interval}d</strong></p>
                  </div>
                  <div className="text-right">
                    <p>{t('review.ease')} <strong className="text-base-content">{currentCard.ease_factor.toFixed(2)}</strong></p>
                  </div>
                  <div className="text-right">
                    <p>{t('review.streak')} <strong className="text-base-content">{currentCard.success_streak}</strong></p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-base-content/50 mb-2">
                      {t('review.word')}
                    </p>
                    <h2 className="text-4xl font-semibold text-primary">
                      {currentCard.word_base_form}
                    </h2>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wide text-base-content/50 mb-2">
                      {t('review.example')}
                    </p>
                    <p className="text-base italic text-base-content/80">
                      {currentCard.example_sentence}
                    </p>
                  </div>

                  {!showAnswer ? (
                    <div className="space-y-4">
                      <p className="text-sm text-base-content/70">
                        {t('review.thinkAndReveal')}
                      </p>
                      <button
                        className="btn btn-primary gap-2 w-full"
                        onClick={handleReveal}
                      >
                        <FaEye /> {t('review.revealAnswer')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-6 bg-base-200 rounded-lg">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-base-content/50 mb-1">
                            {t('review.translation')}
                          </p>
                          <p className="text-xl font-medium">
                            {currentCard.word_translation}
                          </p>
                          {currentCard.grammar && (
                            <p className="text-sm text-base-content/60 mt-1">{currentCard.grammar}</p>
                          )}
                        </div>
                        {currentCard.user_comment && (
                          <p className="text-sm text-base-content/60 mt-2">
                            {t('review.notes')} {currentCard.user_comment}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {RATING_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            className={`btn btn-sm transition ${option.className} flex-1`}
                            disabled={isSubmitting}
                            onClick={() => handleRating(option.value)}
                            title={t(option.descriptionKey)}
                          >
                            <span className="font-semibold">{t(option.labelKey)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center text-base-content/60">
              <FaCheckCircle className="text-4xl text-success" />
              <p className="text-lg font-medium">{t('review.allCaughtUp')}</p>
              <p className="text-sm">
                {t('review.addMoreWords')}
              </p>
            </div>
          )}

        </div>

        <Toast.Root className="ToastRoot" open={toastOpen} onOpenChange={setToastOpen}>
          <Toast.Title className="ToastTitle">
            {reviewSummary && t('review.ratedAs', { rating: reviewSummary.ratingLabel })}
          </Toast.Title>
          <Toast.Description className="ToastDescription">
            {reviewSummary && t('review.nextReview', { when: reviewSummary.nextReviewText, interval: reviewSummary.interval })}
          </Toast.Description>
        </Toast.Root>
        <Toast.Viewport className="ToastViewport" />
      </div>
    </Toast.Provider>
  );
};

export default ReviewPage;
